import type { ReactNode } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';

export type CommandAction = {
  id: string;
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
};

export type CommandBarProps = {
  actions?: CommandAction[];
  primaryAction?: CommandAction;
  overflowActions?: CommandAction[];
};

/** Page-level action button group for list and hub headers. */
export function CommandBar({ actions = [], primaryAction, overflowActions = [] }: CommandBarProps) {
  const renderAction = (action: CommandAction, variant: 'default' | 'outline' | 'destructive' = 'outline') => {
    if (action.href) {
      return (
        <Button key={action.id} variant={variant} asChild disabled={action.disabled}>
          <a href={action.href}>{action.label}</a>
        </Button>
      );
    }
    return (
      <Button
        key={action.id}
        variant={action.variant === 'destructive' ? 'destructive' : variant}
        onClick={action.onClick}
        disabled={action.disabled}
      >
        {action.label}
      </Button>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.map((a) => renderAction(a))}
      {primaryAction && renderAction(primaryAction, 'default')}
      {overflowActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" aria-label="More actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflowActions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                onClick={action.onClick}
                disabled={action.disabled}
                className={action.variant === 'destructive' ? 'text-destructive' : undefined}
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export type QuickActionsPanelProps = {
  title?: string;
  children: ReactNode;
};

export function QuickActionsPanel({ title = 'Quick Actions', children }: QuickActionsPanelProps) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}
